import {AxiosError} from "axios";
import {useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from "react-bootstrap/InputGroup";
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {PGPKeyServerClientApi, type PGPKeyServerKeyDetails, PGPKeyServerSearchResult} from "../api/admin/generated";
import {DialogCloseCause} from "../common/Enums";
import {RenderAlert} from "../common/RenderAlert";
import {Truncate} from "../common/Truncate";
import {DateUtils} from "../utils/DateUtils";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";

/*
 * REST api for accessing the back-end
 */
const pgpKeyServerClientApi = new PGPKeyServerClientApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface PGPKeyServerDialogProps {
    onClose : OnClose
}

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
}

const Column = ({value, children, className, maxLength = 60}: ColumnProps) => {
    return (
        <td className={"user-select-all " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const PGPKeyServerDialog = ({onClose} : PGPKeyServerDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The search query
     */
    const [searchValue, setSearchValue] = useState<string>();
    /**
     * If true, only exact matches will be returned from the Key Server
     */
    const [exactMatch, setExactMatch] = useState<boolean>(false);
    /**
     * If true, the keys will be trusted when imported
     */
    const [autoTrustOnImport, setAutoTrustOnImport] = useState<boolean>(false);
    /**
     * Set to true while importing to show a spinner on the import button
     */
    const [importing, setImporting] = useState<boolean>(false);
    /**
     * Set to true while searching to show a spinner on the import button
     */
    const [searching, setSearching] = useState<boolean>(false);
    /**
     * Set to true while searching to show a spinner on the import button
     */
    const [searchResult, setSearchResult] = useState<PGPKeyServerSearchResult>();
    /**
     * The selected keys
     */
    const [selectedKeys, setSelectedKeys] = useState<PGPKeyServerKeyDetails[]>([]);

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    const searchKeys = () =>
    {
        // clear error message
        setErrorMessage(undefined);
        setSearching(true);

        pgpKeyServerClientApi.pGPKeyServerClientControllerSearchKeys(searchValue??"", exactMatch, 100)
            .then((response) =>
            {
                const result : PGPKeyServerSearchResult = response.data;

                setSearchResult(result);

                if (result.errors.length > 0) {
                    setErrorMessage(result.errors.join(", "))
                }
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error searching keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setSearching(false);
            });
    }

    const importKeys = () =>
    {
        setImporting(true);

        pgpKeyServerClientApi.pGPKeyServerClientControllerDownloadKeys(autoTrustOnImport, selectedKeys.map(k => k.keyID))
            .then(() => {
                onClose(DialogCloseCause.Succes);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error importing keys", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                setImporting(false);
            });
    }

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        onClose(DialogCloseCause.Cancel);
    }

    /**
     * Called when a key is selected (i.e., when the checkbox is checked)
     */
    const onSelectKey = (checked : boolean, key : PGPKeyServerKeyDetails) =>
    {
        if (checked) {
            // add to selected
            setSelectedKeys([...selectedKeys, key]);
        }
        else {
            // remove from selected
            setSelectedKeys(selectedKeys.filter(e => e !== key));
        }
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} size="xl" fullscreen="lg-down" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("pgp-keyserver-dialog.find-keys-remote")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                <Form>
                    <Form.Group className="mb-3">
                        <InputGroup>
                            <Form.Control size="sm"
                                          aria-label={t("pgp-keyserver-dialog.search-value")}
                                          type="search"
                                          placeholder={t("pgp-keyserver-dialog.search-query")}
                                          value={searchValue}
                                          onChange={e => setSearchValue(e.target.value)}
                            />
                            <Button variant="primary"
                                    onClick={searchKeys}
                                    disabled={(searchValue??"").length === 0}>
                                {searching && <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    aria-hidden="true"
                                    className="me-1"
                                />}
                                {searching ? t("common.searching") : t("common.search")}
                            </Button>
                        </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            checked={exactMatch}
                            onChange={(e) => setExactMatch(e.target.checked)}
                            label={t("pgp-keyserver-dialog.exact-match")}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            type="checkbox"
                            checked={autoTrustOnImport}
                            onChange={(e) => setAutoTrustOnImport(e.target.checked)}
                            label={t("pgp-keyserver-dialog.auto-trust-on-import")}
                        />
                    </Form.Group>

                    <Table bordered hover responsive>
                        <thead className="sticky-top">
                        <tr className="text-nowrap">
                            <th></th>
                            <th>{t("pgp-keyserver-dialog.user-ids")}</th>
                            <th>{t("pgp-keyserver-dialog.key-id")}</th>
                            <th>{t("pgp-keyserver-dialog.algorithm")}</th>
                            <th>{t("pgp-keyserver-dialog.key-length")}</th>
                            <th>{t("pgp-keyserver-dialog.creation-date")}</th>
                            <th>{t("pgp-keyserver-dialog.expiration-date")}</th>
                            <th>{t("pgp-keyserver-dialog.key-flags")}</th>
                            <th>{t("pgp-keyserver-dialog.key-server-url")}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {searchResult?.keys.map((key: PGPKeyServerKeyDetails) => (
                            <tr key={key.keyID}>
                                <td>
                                    <Form.Check
                                        type="checkbox"
                                        className="me-0"
                                        checked={selectedKeys.includes(key)}
                                        onChange={(e) => onSelectKey(e.target.checked, key)}
                                        inline
                                    />
                                </td>
                                <Column value={key.userIDs.join(", ")}/>
                                <Column value={key.keyID}/>
                                <Column value={key.algorithm}/>
                                <Column value={`${key.keyLength}`}/>
                                <Column value={DateUtils.timestampToDateStringShort(key.creationDate)}/>
                                <Column value={DateUtils.timestampToDateStringShort(key.expirationDate)}/>
                                <Column value={key.keyFlags}/>
                                <Column value={key.keyServerURL}/>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={importKeys}
                        disabled={selectedKeys.length === 0}>
                    {importing && <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        aria-hidden="true"
                        className="me-1"
                    />}
                    {importing ? t("common.importing") : t("common.import")}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
